/**
 * "Mark as Submitted" dialog (Specs/win-loss-harvest-spec.md §1.2)
 *
 * Harvest selection at the → submitted transition: eligible artifacts ticked
 * by default, ineligible ones listed disabled with their state so everything
 * is accounted for. "Skip" submits without harvesting — the status change is
 * never blocked. Owns the pluggable pre-submission check-row slot (soft
 * warnings, never gates). Three contributors today: the compliance coverage
 * gate, the compliance position rows — mandatory at-risk counts and AI-set
 * unreviewed mandatory Complies (compliance loop spec §7) — and the Themes
 * review gap.
 *
 * R3 T6b (Mark lodged): the dialog also lists the exact file each checklist
 * item will send, pre-ticked, with the lodged date and time (defaults to now)
 * and an optional reference number or receipt. On confirm those files are
 * frozen FIRST — copied, hashed, recorded — and only then does the existing
 * status move and harvest run, exactly as before. A failed freeze moves
 * nothing. Downloading files never marks a bid lodged; only this does.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Library, AlertTriangle } from 'lucide-react'
import { LodgeFilesFields, type LodgeReceipt } from '@/components/harvest/LodgeFilesFields'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useHarvestStore } from '@/stores/harvest-store'
import { useCoverageStore } from '@/stores/coverage-store'
import { useSubmissionFilesStore } from '@/stores/submission-files-store'
import { getLatestThemesRun } from '@/lib/review-api'
import { ApiRequestError, getComplianceMatrixItems } from '@/lib/api'
import {
  fromDateTimeLocalValue,
  lodgeFilesFor,
  toDateTimeLocalValue,
} from '@/lib/submission-files-text'
import {
  MANDATORY_UNRESOLVED_STATES,
  complianceMatrixLink,
  countMandatoryRisk,
  reviewCountPhrase,
  submissionRelevant,
} from '@/lib/compliance-position'
import type { ExtractedComplianceItem } from '@/types/extraction'

export function MarkAsSubmittedDialog() {
  const dialog = useHarvestStore((s) => s.submitDialog)
  const [missingThemes, setMissingThemes] = useState(0)
  const status = useHarvestStore((s) => s.status)
  const isSaving = useHarvestStore((s) => s.isSaving)
  const confirmSubmit = useHarvestStore((s) => s.confirmSubmit)
  const skipSubmit = useHarvestStore((s) => s.skipSubmit)
  const closeSubmitDialog = useHarvestStore((s) => s.closeSubmitDialog)

  const loadCoverage = useCoverageStore((s) => s.load)
  const coverage = useCoverageStore((s) => s.coverage)
  const coverageBidId = useCoverageStore((s) => s.bidId)

  const artifacts = useMemo(
    () => (dialog ? status?.artifacts ?? [] : []),
    [dialog, status]
  )
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [positionItems, setPositionItems] = useState<ExtractedComplianceItem[]>([])

  // Mark lodged (R3 T6b): the files each checklist item will send.
  const filesBidId = useSubmissionFilesStore((s) => s.bidId)
  const filesData = useSubmissionFilesStore((s) => s.data)
  const filesError = useSubmissionFilesStore((s) => s.error)
  const loadFiles = useSubmissionFilesStore((s) => s.load)
  const lodge = useSubmissionFilesStore((s) => s.lodge)
  const isLodging = useSubmissionFilesStore((s) => s.isLodging)
  const fileItems = useMemo(
    () => (dialog && filesBidId === dialog.bidId ? filesData?.items ?? [] : []),
    [dialog, filesBidId, filesData]
  )
  const [tickedFiles, setTickedFiles] = useState<Set<string>>(new Set())
  const [lodgedAtValue, setLodgedAtValue] = useState('')
  const [reference, setReference] = useState('')
  const [receipt, setReceipt] = useState<LodgeReceipt | null>(null)
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false)
  // Set once this dialog has frozen its files, so a retried confirm (after the
  // restricted-library warning, say) never freezes them twice.
  const [lodgedRecordId, setLodgedRecordId] = useState<string | null>(null)
  // Freezing failed: the dialog offers "Try again" or submitting without copies,
  // so a storage problem at deadline time never blocks the submission itself.
  const [freezeFailure, setFreezeFailure] = useState<{ kind: 'submit' | 'skip'; detail: string | null } | null>(null)

  // Fresh lodge fields each time the dialog opens; the files load if this bid's aren't in.
  useEffect(() => {
    if (!dialog) return
    setLodgedAtValue(toDateTimeLocalValue(new Date()))
    setReference('')
    setReceipt(null)
    setLodgedRecordId(null)
    setFreezeFailure(null)
    if (useSubmissionFilesStore.getState().bidId !== dialog.bidId || !useSubmissionFilesStore.getState().data) {
      void loadFiles(dialog.bidId)
    }
  }, [dialog, loadFiles])

  // Default selection: every file ticked — once per opening, when the files
  // arrive, so a background re-read never undoes the user's unticks.
  const ticksSetFor = useRef<string | null>(null)
  useEffect(() => {
    if (!dialog) {
      ticksSetFor.current = null
      return
    }
    if (ticksSetFor.current === dialog.bidId || filesBidId !== dialog.bidId || !filesData) return
    ticksSetFor.current = dialog.bidId
    setTickedFiles(new Set(fileItems.filter((item) => item.file).map((item) => item.itemId)))
  }, [dialog, filesBidId, filesData, fileItems])

  // Default selection: every eligible artifact ticked
  useEffect(() => {
    if (!dialog) return
    setSelected(new Set(artifacts.filter((a) => a.eligible).map((a) => a.artifactId)))
  }, [dialog, artifacts])

  // Pre-submission check-row slot: compliance coverage gate (spec §4.6 there)
  useEffect(() => {
    if (dialog) void loadCoverage(dialog.bidId)
  }, [dialog, loadCoverage])

  // Check-row slot: compliance positions (compliance loop spec §7). Soft —
  // a failed fetch contributes nothing, and the rows inform, never block.
  useEffect(() => {
    if (!dialog) {
      setPositionItems([])
      return
    }
    let alive = true
    void getComplianceMatrixItems(dialog.bidId)
      .then((items) => {
        if (alive) setPositionItems(items)
      })
      .catch(() => {
        /* Soft check — absence of data contributes nothing */
      })
    return () => {
      alive = false
    }
  }, [dialog])

  // Check-row slot: Themes review (scoped-brief §12.3) — count planned-theme
  // gaps from the latest non-stale completed Themes run per artifact.
  useEffect(() => {
    if (!dialog) {
      setMissingThemes(0)
      return
    }
    let alive = true
    void (async () => {
      let missing = 0
      for (const artifact of artifacts.slice(0, 6)) {
        try {
          const latest = await getLatestThemesRun(artifact.artifactId)
          if (!latest.run || latest.run.status !== 'complete' || latest.stale) continue
          missing += latest.run.findingCount
        } catch {
          // Soft check — absence of data contributes nothing
        }
      }
      if (alive) setMissingThemes(missing)
    })()
    return () => {
      alive = false
    }
  }, [dialog, artifacts])

  if (!dialog) return null

  const summary =
    coverageBidId === dialog.bidId && coverage
      ? coverage.complianceSummary
      : null
  const criteriaSummary =
    coverageBidId === dialog.bidId && coverage ? coverage.criteriaSummary : null
  const unmappedTotal = (summary?.unmapped ?? 0) + (criteriaSummary?.unmapped ?? 0)
  const notAddressedTotal = summary && criteriaSummary
    ? summary.total + criteriaSummary.total - summary.addressed - criteriaSummary.addressed - (summary.unmapped + criteriaSummary.unmapped)
    : 0

  const positionRisk = countMandatoryRisk(positionItems)
  const unconfirmedComply = submissionRelevant(positionItems).filter(
    (item) =>
      item.criticality === 'mandatory' &&
      item.position === 'comply' &&
      (item.positionSource ?? 'ai') === 'ai'
  ).length

  const toggle = (artifactId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(artifactId)) next.delete(artifactId)
      else next.add(artifactId)
      return next
    })
  }

  const selectedCount = selected.size
  const harvestApplies = !!status?.autoHarvestEnabled && artifacts.length > 0
  const isBusy = isSaving || isLodging || isUploadingReceipt
  // "No files yet" and "couldn't load the files" are different answers.
  const filesReady = filesBidId === dialog.bidId && !!filesData
  const filesLoadFailed = filesBidId === dialog.bidId && !filesData && !!filesError

  const toggleFile = (itemId: string) => {
    setTickedFiles((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  /** The existing transition, exactly as before Mark lodged existed. */
  const runTransition = async (kind: 'submit' | 'skip') => {
    if (kind === 'skip') await skipSubmit()
    else await confirmSubmit([...selected])
  }

  /**
   * Freeze first, then the existing transition. "Skip" skips the library
   * harvest only — the ticked files are still kept. A failed freeze moves
   * nothing and asks: try again, or submit without keeping copies.
   */
  const handleConfirm = async (kind: 'submit' | 'skip') => {
    const files = lodgeFilesFor(fileItems, tickedFiles)
    const trimmedReference = reference.trim()
    const keepsSomething = files.length > 0 || trimmedReference.length > 0 || receipt !== null
    if (keepsSomething && !lodgedRecordId) {
      setFreezeFailure(null)
      try {
        const record = await lodge(dialog.bidId, {
          lodgedAt: fromDateTimeLocalValue(lodgedAtValue) ?? new Date().toISOString(),
          reference: trimmedReference || null,
          receiptDocumentId: receipt?.documentId ?? null,
          files,
        })
        setLodgedRecordId(record.id)
      } catch (error) {
        // A refusal (4xx) carries a reason written for the user; anything else is ours.
        const detail =
          error instanceof ApiRequestError && error.statusCode !== undefined && error.statusCode < 500 && error.message
            ? error.message
            : null
        setFreezeFailure({ kind, detail })
        return
      }
    }
    await runTransition(kind)
  }

  /** "Mark as submitted without keeping copies": the old path, nothing recorded. */
  const handleSubmitWithoutCopies = async () => {
    if (!freezeFailure) return
    const { kind } = freezeFailure
    setFreezeFailure(null)
    await runTransition(kind)
  }

  return (
    <Dialog open onOpenChange={(open) => !open && closeSubmitDialog()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Mark as Submitted</DialogTitle>
          <DialogDescription>{dialog.bidTitle}</DialogDescription>
        </DialogHeader>

        {/* Soft pre-submission checks — inform, never block */}
        {(unmappedTotal > 0 || notAddressedTotal > 0) && (
          <div className="space-y-1.5">
            {unmappedTotal > 0 && (
              <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--status-warning)/0.08)] px-2.5 py-2 text-xs">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-warning))]" />
                <span>
                  {unmappedTotal} requirement{unmappedTotal === 1 ? ' isn’t' : 's aren’t'} mapped to any
                  section.{' '}
                  <Link
                    to={`/app/bids/${dialog.bidId}/qualify?view=compliance_matrix`}
                    className="text-[hsl(var(--link))] hover:underline"
                    onClick={closeSubmitDialog}
                  >
                    Open compliance checklist
                  </Link>
                </span>
              </div>
            )}
            {unmappedTotal === 0 && notAddressedTotal > 0 && (
              <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--status-warning)/0.08)] px-2.5 py-2 text-xs">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-warning))]" />
                <span>
                  {notAddressedTotal} mapped requirement{notAddressedTotal === 1 ? '' : 's'} not yet
                  addressed — submit anyway?
                </span>
              </div>
            )}
          </div>
        )}

        {positionRisk.atRisk > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--status-warning)/0.08)] px-2.5 py-2 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--destructive))]" />
            <span>
              {positionRisk.nonComply > 0 && (
                <Link
                  to={complianceMatrixLink(dialog.bidId, {
                    position: 'non_comply',
                    criticality: 'mandatory',
                  })}
                  className="text-[hsl(var(--link))] hover:underline"
                  onClick={closeSubmitDialog}
                >
                  {positionRisk.nonComply} mandatory non-comply
                </Link>
              )}
              {positionRisk.nonComply > 0 && positionRisk.needsReview > 0 && ' · '}
              {positionRisk.needsReview > 0 && (
                <Link
                  to={complianceMatrixLink(dialog.bidId, {
                    // Every unresolved label the count includes: "Needs review", "Needs your confirmation", "Not assessed".
                    position: MANDATORY_UNRESOLVED_STATES.join(','),
                    criticality: 'mandatory',
                  })}
                  className="text-[hsl(var(--link))] hover:underline"
                  onClick={closeSubmitDialog}
                >
                  {positionRisk.needsReview} mandatory{' '}
                  {reviewCountPhrase(
                    positionRisk.needsReview,
                    positionRisk.needsConfirmation,
                    'needs review'
                  )}
                </Link>
              )}
              {' — pass/fail requirements. Submit anyway?'}
            </span>
          </div>
        )}

        {unconfirmedComply > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--status-warning)/0.08)] px-2.5 py-2 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-warning))]" />
            <span>
              {unconfirmedComply} mandatory Comply position
              {unconfirmedComply === 1 ? ' is' : 's are'} AI-set and unreviewed.{' '}
              <Link
                to={complianceMatrixLink(dialog.bidId, {
                  position: 'comply',
                  criticality: 'mandatory',
                  source: 'ai',
                })}
                className="text-[hsl(var(--link))] hover:underline"
                onClick={closeSubmitDialog}
              >
                Review and confirm
              </Link>
            </span>
          </div>
        )}

        {missingThemes > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--status-warning)/0.08)] px-2.5 py-2 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-warning))]" />
            <span>
              {missingThemes} planned theme{missingThemes === 1 ? '' : 's'} missing from the draft —
              review before submitting?
            </span>
          </div>
        )}

        {filesLoadFailed ? (
          <div role="alert" className="space-y-3 rounded-lg bg-destructive/10 px-3 py-2.5">
            <p className="text-sm">
              We couldn&apos;t load the files to send, so BidContext can&apos;t keep copies of them right now.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" disabled={isBusy} onClick={() => void runTransition('submit')}>
                Mark as submitted without keeping copies
              </Button>
              <Button disabled={isBusy} onClick={() => void loadFiles(dialog.bidId)}>
                Try again
              </Button>
            </div>
          </div>
        ) : (
          <LodgeFilesFields
            isLoading={!filesReady}
            bidId={dialog.bidId}
            items={fileItems}
            ticked={tickedFiles}
            onToggle={toggleFile}
            lodgedAt={lodgedAtValue}
            onLodgedAtChange={setLodgedAtValue}
            reference={reference}
            onReferenceChange={setReference}
            receipt={receipt}
            onReceiptChange={setReceipt}
            onUploadingChange={setIsUploadingReceipt}
            disabled={isBusy}
          />
        )}

        {/* Harvest block */}
        {harvestApplies && (
          <div>
            <div className="flex items-center gap-1.5 text-sm">
              <Library className="h-4 w-4 text-muted-foreground" />
              <span>
                {selectedCount === 0
                  ? 'No documents will be added to your library'
                  : `${selectedCount} document${selectedCount === 1 ? '' : 's'} will be added to your library as past-bid content`}
              </span>
            </div>
            <div className="mt-2 space-y-1.5">
              {artifacts.map((artifact) => (
                <label
                  key={artifact.artifactId}
                  className={`flex items-start gap-2 text-sm ${artifact.eligible ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                >
                  <Checkbox
                    checked={selected.has(artifact.artifactId)}
                    disabled={!artifact.eligible}
                    onCheckedChange={() => toggle(artifact.artifactId)}
                    aria-label={artifact.name}
                    className="mt-0.5"
                  />
                  <span className="min-w-0">
                    <span className="block truncate">{artifact.name}</span>
                    {!artifact.eligible && artifact.ineligibleReason && (
                      <span className="block text-meta">
                        {artifact.ineligibleReason} — not eligible
                      </span>
                    )}
                    {artifact.eligible && artifact.harvestedDocumentId && (
                      <span className="block text-meta">
                        Already in your library — will be updated
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {freezeFailure ? (
          <div role="alert" className="space-y-3 rounded-lg bg-destructive/10 px-3 py-2.5">
            <p className="text-sm">
              BidContext couldn&apos;t keep copies of the files, so the bid hasn&apos;t been marked as submitted yet.
              {freezeFailure.detail ? ` ${freezeFailure.detail}` : ''}
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" disabled={isBusy} onClick={() => void handleSubmitWithoutCopies()}>
                Mark as submitted without keeping copies
              </Button>
              <Button disabled={isBusy} onClick={() => void handleConfirm(freezeFailure.kind)}>
                {isLodging ? 'Trying again…' : 'Try again'}
              </Button>
            </div>
          </div>
        ) : filesLoadFailed ? null : (
          <DialogFooter className="gap-2">
            {harvestApplies && (
              <Button variant="ghost" disabled={isBusy} onClick={() => void handleConfirm('skip')}>
                Skip
              </Button>
            )}
            <Button disabled={isBusy} onClick={() => void handleConfirm('submit')}>
              {isBusy ? 'Submitting…' : 'Submit bid'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
